import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { BiSolidEditAlt } from "react-icons/bi";
import { FaLocationDot, FaStar } from "react-icons/fa6";
import {
  IoArrowBackSharp,
  IoChevronDown,
  IoClose,
  IoLocationOutline,
  IoSwapVertical,
} from "react-icons/io5";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import MainLayout from "@/Component/MainLayout";
import CartNavLink from "@/Component/Common/CartNavLink";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import featuredStyles from "@/Component/Common/FeaturedPackage/FeaturedPackage.module.css";
import styles from "../HolidayListing/HolidayListing.module.css";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import useActivityEditSearch from "@/hooks/useActivityEditSearch";
import DesktopListingSearch from "./DesktopListingSearch";
import LocationSearchScreen from "./LocationSearchScreen";
import { getHeaderSearchSummary } from "@/utils/activitySearch";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchActivitiesList,
  fetchActivityFilters,
} from "@/store/slices/activitySlice";
import {
  buildActivityListPayload,
  buildFilterState,
  buildReviewBands,
  getUrlFilterDefaults,
  getUrlPageTitle,
  normalizeActivityFilters,
  parseUrlListParams,
  pickUrlApiParams,
} from "@/utils/activityListing";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { withRegionPath } from "@/utils/session";
import {
  canRestoreListingRange,
  clampFilterRange,
  consumeListingSearchReset,
  getFilterBoundsKey,
  getListingSearchKey,
  isSameListingSearch,
  loadListingFilters,
  mergePresentUrlDefaults,
  saveListingFilters,
  snapInclusiveRangeValue,
  useListingSearchEpoch,
} from "@/utils/listingFilterSession";
import DownloadNH from "@/Component/Common/DownloadNH";
import SeeTheWorld from "@/Component/Common/SeeTheWorld";
import { getReviewCount, getSeatsLeftLabel } from "@/utils/packageCardMeta";

const PAGE_SIZE = 10;

const EMPTY_SELECTION = {
  activityTypeIds: [],
  subTypeIds: [],
  cityIds: [],
  activityIds: [],
  cancellationPolicyIds: [],
  includesTypeIds: [],
  timeOfDayValues: [],
  reviewRating: 0,
  instantConfirm: "",
};

function toggleId(list, id) {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function toggleValue(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function mapActivity(rawInput = {}) {
  const raw = rawInput?.activity || rawInput;
  const includeType = Array.isArray(raw.includeType) ? raw.includeType : [];
  const category = String(raw.activitiesType || "").replace(/\s+/g, " ").trim();
  const cityName = String(raw.city || "").trim();
  const countryName = String(raw.country || "").trim();
  const location = [cityName, countryName].filter(Boolean).join(", ");
  const durationLabel = String(raw.duration || "").trim();

  return {
    id: raw.activityId || raw.slug,
    title: raw.activityName || "",
    categories: category,
    location,
    duration: durationLabel,
    rating: Number(raw.rating) || 0,
    ratingLabel: raw.reviewRemark || "",
    reviews: getReviewCount(raw),
    seatsLeft: getSeatsLeftLabel(raw),
    tagOnThumbnail: String(raw.tagOnThumbnail || "").trim(),
    discountLabel: raw.tag2 || "",
    priceOld: Number(raw.displayCutPrice) || Number(raw.price) || 0,
    price: Number(raw.price) || 0,
    image: raw.imagePath || "",
    href: withRegionPath(`/activity/${raw.slug || raw.activityId}`),
    includeType,
  };
}

function PriceDisplay({ value, variant = "main", size = 14 }) {
  return (
    <>
      {getCurrencySymbol(variant, size)}
      {formatIndianPrice(value)}
    </>
  );
}

function CardImageOverlays({ tagOnThumbnail, seatsLeft, duration }) {
  return (
    <>
      {tagOnThumbnail ? (
        <span className={styles.thumbnailTag}>
          <FaStar className={styles.thumbnailTagIcon} aria-hidden="true" />
          <span className={styles.thumbnailTagText}>{tagOnThumbnail}</span>
        </span>
      ) : null}
      {seatsLeft ? <span className={styles.badgeSeats}>{seatsLeft}</span> : null}
      {duration ? <span className={styles.durationBadge}>{duration}</span> : null}
    </>
  );
}

function ResponsiveActivityCard({ item }) {
  const includeIcons = Array.isArray(item.includeType) ? item.includeType : [];

  return (
    <article className={`${featuredStyles.card} ${styles.responsiveCard}`}>
      <a href={item.href} className={featuredStyles.imageLink} aria-label={item.title}>
        <div className={featuredStyles.imageWrap}>
          <img src={item.image} alt={item.title} className={featuredStyles.cardImage} />

          {item.tagOnThumbnail ? (
            <span className={featuredStyles.badgeBest}>
              <FaStar style={{ marginTop: "-2px" }} />
              {item.tagOnThumbnail}
            </span>
          ) : null}
          {item.seatsLeft ? (
            <span className={featuredStyles.badgeSeats}>{item.seatsLeft}</span>
          ) : null}

          {item.rating ? (
            <span className={featuredStyles.rating}>
              <FaStar color="#FFB835" />
              <strong style={{ color: "#0B192C" }}>{item.rating}</strong>
              {item.reviews != null ? ` (${item.reviews})` : null}
            </span>
          ) : null}
        </div>
      </a>

      <div className={featuredStyles.body}>
        {/* {item.categories ? (
          <p className={featuredStyles.cardCategory}>{item.categories}</p>
        ) : null} */}
        <h3 className={featuredStyles.cardTitle}>
          <a href={item.href} className={featuredStyles.titleLink}>{item.title}</a>
        </h3>
        {item.location ? (
          <p className={featuredStyles.cardSubtitle}>
            <FaLocationDot size={10} /> {item.location}
          </p>
        ) : null}

        <div className={featuredStyles.cardIncludes}>
          <div className={featuredStyles.inclusion}>
            {includeIcons.map((feature, index) => (
              <span
                key={`${feature.includeName || "inc"}-${index}`}
                className={featuredStyles.includeIcon}
                title={feature.includeName || ""}
              >
                {feature.imagePath ? (
                  <img src={feature.imagePath} alt={feature.includeName || ""} />
                ) : null}
              </span>
            ))}
          </div>
        </div>

        <div className={featuredStyles.footer}>
          {item.discountLabel ? (
            <span className={featuredStyles.discountTag}>{item.discountLabel}</span>
          ) : null}
          <div className={featuredStyles.footerContent}>
            <div className={featuredStyles.priceBlock}>
              <p className={featuredStyles.starting}>
                Starting From
                <span className={featuredStyles.oldPrice}>
                  <PriceDisplay value={item.priceOld} variant="sub" size={10} />
                </span>
              </p>
              <p className={featuredStyles.price}>
                <PriceDisplay value={item.price} size={14} />
                <span className={featuredStyles.perPerson}>/Person</span>
              </p>
            </div>
            <a href={item.href} className={featuredStyles.viewDealsBtn}>
              View Details
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

function ActivityCard({ item, mobile }) {
  const includeIcons = Array.isArray(item.includeType) ? item.includeType : [];

  if (mobile) {
    return <ResponsiveActivityCard item={item} />;
  }

  return (
    <article className={styles.card}>
      <div className={styles.cardContent}>
        <a href={item.href} className={styles.cardImage} aria-label={item.title}>
          <img src={item.image} alt={item.title} />
          <CardImageOverlays
            tagOnThumbnail={item.tagOnThumbnail}
            seatsLeft={item.seatsLeft}
            duration={item.duration}
          />
        </a>
        <div className={styles.cardDetails}>
          <div className={styles.cardTop}>
            <div className={styles.cardBasic}>
              {item.categories ? <p className={styles.categories}>{item.categories}</p> : null}
              <h3 className={styles.cardTitle}>
                <a href={item.href} className={styles.titleLink}>{item.title}</a>
              </h3>
              {item.location ? (
                <p className={styles.itinerary}>
                  <FaLocationDot size={12} /> {item.location}
                </p>
              ) : null}
            </div>
            {item.rating ? (
              <div className={styles.ratingBlock}>
                <span className={styles.ratingScore}>{item.rating}</span>
                <div>
                  {item.ratingLabel ? (
                    <p className={styles.ratingLabel}>{item.ratingLabel}</p>
                  ) : null}
                  {item.reviews != null ? (
                    <p className={styles.reviews}>{item.reviews} Reviews</p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          {includeIcons.length ? (
            <div className={styles.inclusionsRow}>
              {includeIcons.map((feature, index) => (
                <span
                  key={`${feature.includeName || "inc"}-${index}`}
                  className={styles.inclusionIcon}
                  title={feature.includeName || ""}
                >
                  {feature.imagePath ? (
                    <img src={feature.imagePath} alt={feature.includeName || ""} />
                  ) : null}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.cardPrice}>
        {item.discountLabel ? <span className={styles.offerBadge}>{item.discountLabel}</span> : null}
        {/* <p className={styles.priceOld}>
          <span className={styles.startsFrom}>Starts From</span>
          <PriceDisplay
            value={item.priceOld}
            variant="sub"
            size={12}
          />
        </p> */}
        <div className={styles.startpriceOld}>
          Starts From
          <p className={styles.priceOld}>
            <PriceDisplay value={item.priceOld} variant="sub" size={10} />
          </p>
        </div>
        <p className={styles.priceNow}>
          <PriceDisplay value={item.price} size={16} />
        </p>
        <p className={styles.priceNote}>Per Person</p>
        <a href={item.href} className={styles.viewBtn}>
          View Details
        </a>
      </div>
    </article>
  );
}

function CheckboxList({
  options,
  selectedIds,
  onToggle,
  selectedValues,
  onToggleValue,
  initialLimit = 0,
}) {
  const [expanded, setExpanded] = useState(false);

  if (!options?.length) return null;

  const hasMore = initialLimit > 0 && options.length > initialLimit;
  const visibleOptions =
    hasMore && !expanded ? options.slice(0, initialLimit) : options;

  return (
    <>
      <ul className={styles.checkList}>
        {visibleOptions.map((option) => {
          const value = option.value ?? option.id;
          const checked = selectedIds
            ? selectedIds.includes(option.id)
            : selectedValues?.includes(value);
          return (
            <li key={option.id}>
              <label className={styles.checkRow}>
                <span className={styles.checkLabel}>{option.label}</span>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    selectedIds ? onToggle(option.id) : onToggleValue(value)
                  }
                />
              </label>
            </li>
          );
        })}
      </ul>
      {hasMore ? (
        <button
          type="button"
          className={styles.viewMoreBtn}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "View Less" : "View More"}
        </button>
      ) : null}
    </>
  );
}

function ToggleButtonGroup({ options, selectedValues, onToggle, single = false }) {
  if (!options?.length) return null;

  return (
    <div className={styles.flightToggle}>
      {options.map((option) => {
        const value = option.value ?? option.id;
        const active = selectedValues.includes(value);
        return (
          <button
            key={value}
            type="button"
            className={active ? styles.flightActive : ""}
            onClick={() => onToggle(value, single)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CollapsibleBlock({ title, open, onToggle, children }) {
  return (
    <section className={styles.filterBlock}>
      <button
        type="button"
        className={styles.themeToggle}
        onClick={onToggle}
        aria-expanded={open}
      >
        <h3>{title}</h3>
        <span className={open ? styles.chevronUp : ""}>
          <IoChevronDown size={20} />
        </span>
      </button>
      {open ? children : null}
    </section>
  );
}

function getRangeLabelStyle(percent) {
  if (percent <= 10) {
    return { left: `${percent}%`, transform: "translateX(0)" };
  }
  if (percent >= 90) {
    return { left: `${percent}%`, transform: "translateX(-100%)" };
  }
  return { left: `${percent}%`, transform: "translateX(-50%)" };
}

function DualRangeSlider({ min, max, value, onChange, step = 1, formatLabel, fixedLabels = false }) {
  const [minValue, maxValue] = value;
  const range = max - min || 1;
  const minPercent = ((minValue - min) / range) * 100;
  const maxPercent = ((maxValue - min) / range) * 100;

  const handleMinChange = (event) => {
    const next = snapInclusiveRangeValue(event.target.value, min, max, step);
    onChange([Math.min(next, maxValue), maxValue]);
  };

  const handleMaxChange = (event) => {
    const next = snapInclusiveRangeValue(event.target.value, min, max, step);
    onChange([minValue, Math.max(next, minValue)]);
  };

  return (
    <div className={styles.dualRange}>
      <div className={styles.dualRangeTrack}>
        <div className={styles.dualRangeRail} aria-hidden="true" />
        <div
          className={styles.dualRangeFill}
          style={{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }}
          aria-hidden="true"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={minValue}
          onChange={handleMinChange}
          className={`${styles.dualRangeInput} ${styles.dualRangeInputMin}`}
          aria-label="Minimum value"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxValue}
          onChange={handleMaxChange}
          className={`${styles.dualRangeInput} ${styles.dualRangeInputMax}`}
          aria-label="Maximum value"
        />
      </div>
      <div className={`${styles.dualRangeValues} ${fixedLabels ? styles.dualRangeValuesFixed : ""}`}>
        {fixedLabels ? (
          <>
            <span className={styles.dualRangeValue}>{formatLabel(minValue)}</span>
            <span className={styles.dualRangeValue}>{formatLabel(maxValue)}</span>
          </>
        ) : (
          <>
            <span className={styles.dualRangeValue} style={getRangeLabelStyle(minPercent)}>
              {formatLabel(minValue)}
            </span>
            <span className={styles.dualRangeValue} style={getRangeLabelStyle(maxPercent)}>
              {formatLabel(maxValue)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function MobileSheet({
  title,
  onClose,
  onClear,
  children,
  footerLabel = "Apply",
  onApply,
  showFooter = true,
  footerLoading = false,
}) {
  return (
    <div className={styles.sheetRoot}>
      <button type="button" className={styles.sheetOverlay} aria-label="Close" onClick={onClose} />
      <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.sheetGrab} aria-hidden="true" />
        {title === "Filters" &&
          <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: "10px" }}>
            <button type="button" className={styles.sheetCloseBtn} onClick={onClose} aria-label="Close">
              <IoClose />
            </button>
          </div>
        }
        <div className={styles.sheetHeader}>
          <h2>{title}</h2>
          {onClear ? (
            <button type="button" className={styles.sheetClearBtn} onClick={onClear}>
              Clear
            </button>
          ) : (
            <button type="button" className={styles.sheetCloseBtn} onClick={onClose} aria-label="Close">
              <IoClose />
            </button>
          )}
        </div>
        <div className={styles.sheetBody}>{children}</div>
        {showFooter && onApply ? (
          <div className={styles.sheetFooter}>
            <button
              type="button"
              className={styles.sheetApplyBtn}
              onClick={onApply}
              disabled={footerLoading}
            >
              {footerLoading ? (
                <span className={styles.searchBtnSpinner} aria-hidden="true" />
              ) : (
                footerLabel
              )}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SortSheet({ options, value, onChange, onClose, onApply }) {
  return (
    <MobileSheet title="Sort By" onClose={onClose} onApply={onApply}>
      <div className={styles.sortSheetList}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`${styles.sortSheetOption} ${value === option.value ? styles.sortSheetOptionActive : ""
              }`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </MobileSheet>
  );
}

function RangeFilterSheet({ title, children, onClose, onApply }) {
  return (
    <MobileSheet title={title} onClose={onClose} onApply={onApply}>
      <div className={styles.quickFilterSheet}>{children}</div>
    </MobileSheet>
  );
}

function ListingLoader({ fullPage = false, compact = false }) {
  return (
    <div
      className={`${styles.loadingWrap} ${fullPage ? styles.loadingWrapFull : ""} ${compact ? styles.loadingWrapBottom : ""
        }`}
    >
      <img src="/assets/loading.gif" alt="Loading" className={styles.loadingGif} />
    </div>
  );
}

function ListLoadSentinel({ hasMore, loading, loadingMore, onLoadMore }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root: null, rootMargin: "240px", threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  if (!hasMore) return null;

  return (
    <div ref={sentinelRef} className={styles.listSentinel} aria-hidden="true">
      {loadingMore ? <ListingLoader compact /> : null}
    </div>
  );
}

function SearchSheet({ destination, onDestinationClick, onClose, onApply, isSearching = false }) {
  return (
    <MobileSheet
      title="Edit Search"
      onClose={onClose}
      footerLabel="Search"
      onApply={onApply}
      footerLoading={isSearching}
    >
      <div className={styles.searchSheetFields}>
        <button
          type="button"
          className={`${styles.searchField} ${styles.searchFieldBtn}`}
          onClick={onDestinationClick}
        >
          <IoLocationOutline />
          <span className={styles.searchFieldValue}>{destination || "Search destination"}</span>
        </button>
      </div>
    </MobileSheet>
  );
}

function FiltersPanel({
  options,
  selection,
  ranges,
  openSections,
  handlers,
  onToggleSection,
  showApply,
  onApply,
  compact = false,
}) {
  const {
    onToggleActivityType,
    onToggleSubType,
    onToggleCity,
    onToggleInclude,
    onToggleTimeOfDay,
    onToggleCancellation,
    onReviewRating,
    onToggleInstantConfirm,
    onBudget,
    onClear,
  } = handlers;

  const budgetStep =
    ranges.price.max - ranges.price.min > 1000
      ? Math.max(1, Math.round((ranges.price.max - ranges.price.min) / 100))
      : 1;

  return (
    <div className={`${styles.filtersInner} ${compact ? styles.filtersInnerCompact : ""}`}>
      {!compact ? (
        <div className={styles.filtersHead}>
          <h2>Filters</h2>
          <button type="button" className={styles.clearBtn} onClick={onClear}>
            Clear
          </button>
        </div>
      ) : null}

      <div className={styles.filterDesc}>
        {options.includes.length ? (
          <section className={styles.filterBlock}>
            <h3>Popular filters</h3>
            <CheckboxList
              options={options.includes}
              selectedIds={selection.includesTypeIds}
              onToggle={onToggleInclude}
            />
          </section>
        ) : null}

        {ranges.hasPriceRange ? (
          <section className={styles.filterBlock}>
            <h3>Budget</h3>
            <DualRangeSlider
              min={ranges.price.min}
              max={ranges.price.max}
              step={budgetStep}
              value={ranges.budget}
              onChange={onBudget}
              fixedLabels
              formatLabel={(val) => (
                <>
                  {getCurrencySymbol("main", 12)}
                  {formatIndianPrice(val)}
                </>
              )}
            />
          </section>
        ) : null}

        {options.activityTypes.length ? (
          <CollapsibleBlock
            title="Category"
            open={openSections.categories}
            onToggle={() => onToggleSection("categories")}
          >
            <CheckboxList
              options={options.activityTypes}
              selectedIds={selection.activityTypeIds}
              onToggle={onToggleActivityType}
            />
          </CollapsibleBlock>
        ) : null}

        {options.subTypes.length ? (
          <CollapsibleBlock
            title="Sub Types"
            open={openSections.subTypes}
            onToggle={() => onToggleSection("subTypes")}
          >
            <CheckboxList
              options={options.subTypes}
              selectedIds={selection.subTypeIds}
              onToggle={onToggleSubType}
            />
          </CollapsibleBlock>
        ) : null}

        {options.cities.length ? (
          <CollapsibleBlock
            title="Cities"
            open={openSections.cities}
            onToggle={() => onToggleSection("cities")}
          >
            <CheckboxList
              options={options.cities}
              selectedIds={selection.cityIds}
              onToggle={onToggleCity}
              initialLimit={10}
            />
          </CollapsibleBlock>
        ) : null}

        {options.reviewBands.length ? (
          <CollapsibleBlock
            title="Review & Rating"
            open={openSections.reviews}
            onToggle={() => onToggleSection("reviews")}
          >
            <ul className={styles.checkList}>
              {options.reviewBands.map((band) => (
                <li key={band.id}>
                  <label className={styles.checkRow}>
                    <span>{band.label}+ Stars</span>
                    <input
                      type="checkbox"
                      checked={selection.reviewRating === band.min}
                      onChange={() =>
                        onReviewRating(selection.reviewRating === band.min ? 0 : band.min)
                      }
                    />
                  </label>
                </li>
              ))}
            </ul>
          </CollapsibleBlock>
        ) : null}

        {options.cancellationPolicies.length ? (
          <CollapsibleBlock
            title="Cancellation Policy"
            open={openSections.cancellation}
            onToggle={() => onToggleSection("cancellation")}
          >
            <CheckboxList
              options={options.cancellationPolicies}
              selectedIds={selection.cancellationPolicyIds}
              onToggle={onToggleCancellation}
            />
          </CollapsibleBlock>
        ) : null}

        {options.timeOfDay.length ? (
          <section className={styles.filterBlock}>
            <h3>Time of Day</h3>
            <ToggleButtonGroup
              options={options.timeOfDay}
              selectedValues={selection.timeOfDayValues}
              onToggle={onToggleTimeOfDay}
            />
          </section>
        ) : null}

        {options.instantConfirm.length ? (
          <section className={styles.filterBlock}>
            <h3>Instant Confirmation</h3>
            <ToggleButtonGroup
              options={options.instantConfirm}
              selectedValues={selection.instantConfirm ? [selection.instantConfirm] : []}
              onToggle={onToggleInstantConfirm}
              single
            />
          </section>
        ) : null}

        {showApply ? (
          <button type="button" className={styles.applyBtn} onClick={onApply}>
            Apply
          </button>
        ) : null}
      </div>
    </div>
  );
}

function getActivityResetDefaults(urlParams = {}) {
  return {
    ...EMPTY_SELECTION,
    activityTypeIds: urlParams.activityTypeIds || [],
    subTypeIds: urlParams.subTypeIds || [],
  };
}

function useFilterSelection(filterOptions, urlParams = {}, sessionKind) {
  const urlFilterDefaults = useMemo(() => getUrlFilterDefaults(urlParams), [urlParams]);
  const resetDefaults = useMemo(() => getActivityResetDefaults(urlParams), [urlParams]);
  const searchKey = useMemo(
    () => getListingSearchKey(sessionKind, urlParams),
    [sessionKind, urlParams]
  );
  const searchEpoch = useListingSearchEpoch(sessionKind);
  const resetId = `${searchKey}#${searchEpoch}`;
  const allowRestoreRef = useRef(false);

  const [selection, setSelection] = useState(() => resetDefaults);
  const [budget, setBudget] = useState(null);
  const [appliedResetId, setAppliedResetId] = useState("");

  useEffect(() => {
    const stored = loadListingFilters(sessionKind);
    const storedEpoch = Number(stored?.epoch) || 0;
    const forcedReset = consumeListingSearchReset(sessionKind);
    const isNewSearch = forcedReset || searchEpoch > storedEpoch;
    const sameSearch =
      !isNewSearch && isSameListingSearch(stored, sessionKind, searchKey);

    if (sameSearch && stored?.selection && typeof stored.selection === "object") {
      setSelection(
        mergePresentUrlDefaults(
          {
            ...EMPTY_SELECTION,
            ...stored.selection,
          },
          urlFilterDefaults
        )
      );
      allowRestoreRef.current = true;
    } else {
      setSelection(resetDefaults);
      allowRestoreRef.current = false;
    }

    setBudget(null);
    setAppliedResetId(resetId);
  }, [resetId, sessionKind, resetDefaults, urlFilterDefaults, searchEpoch, searchKey]);

  useEffect(() => {
    if (appliedResetId !== resetId) return;

    const stored = loadListingFilters(sessionKind);
    const priceMax = filterOptions.price.max;
    if (priceMax <= 0) return;

    const boundsKey = getFilterBoundsKey({
      priceMin: filterOptions.price.min,
      priceMax,
    });
    const restore =
      allowRestoreRef.current && canRestoreListingRange(stored, boundsKey);

    setBudget(
      restore
        ? clampFilterRange(stored?.budget, filterOptions.price.min, priceMax)
        : [filterOptions.price.min, priceMax]
    );
  }, [appliedResetId, resetId, sessionKind, filterOptions.price.min, filterOptions.price.max]);

  const filterState = useMemo(
    () =>
      buildFilterState({
        activityTypeIds: selection.activityTypeIds,
        subTypeIds: selection.subTypeIds,
        cityIds: selection.cityIds,
        activityIds: selection.activityIds,
        cancellationPolicyIds: selection.cancellationPolicyIds,
        includesTypeIds: selection.includesTypeIds,
        timeOfDay: selection.timeOfDayValues,
        budgetRange: budget || [],
        reviewRating: selection.reviewRating,
        instantConfirm: selection.instantConfirm,
        filterBounds: {
          budgetMin: filterOptions.price.min,
          budgetMax: filterOptions.price.max,
        },
      }),
    [selection, filterOptions, budget]
  );

  const clearSelection = () => {
    setSelection({
      ...EMPTY_SELECTION,
      ...urlFilterDefaults,
    });
    setBudget([filterOptions.price.min, filterOptions.price.max]);
  };

  const handlers = {
    onToggleActivityType: (id) =>
      setSelection((prev) => ({
        ...prev,
        activityTypeIds: toggleId(prev.activityTypeIds, id),
      })),
    onToggleSubType: (id) =>
      setSelection((prev) => ({
        ...prev,
        subTypeIds: toggleId(prev.subTypeIds, id),
      })),
    onToggleCity: (id) =>
      setSelection((prev) => ({ ...prev, cityIds: toggleId(prev.cityIds, id) })),
    onToggleInclude: (id) =>
      setSelection((prev) => ({
        ...prev,
        includesTypeIds: toggleId(prev.includesTypeIds, id),
      })),
    onToggleTimeOfDay: (value) =>
      setSelection((prev) => ({
        ...prev,
        timeOfDayValues: toggleValue(prev.timeOfDayValues, value),
      })),
    onToggleCancellation: (id) =>
      setSelection((prev) => ({
        ...prev,
        cancellationPolicyIds: toggleId(prev.cancellationPolicyIds, id),
      })),
    onReviewRating: (value) =>
      setSelection((prev) => ({ ...prev, reviewRating: value })),
    onToggleInstantConfirm: (value) =>
      setSelection((prev) => ({
        ...prev,
        instantConfirm: prev.instantConfirm === value ? "" : value,
      })),
    onBudget: setBudget,
    onClear: clearSelection,
  };

  return {
    selection,
    budget: budget || [filterOptions.price.min, filterOptions.price.max],
    filterState,
    handlers,
    sessionReady: appliedResetId === resetId && budget != null,
    searchKey,
    searchEpoch,
  };
}

export default function ActivityListing({ initialListPageHeading = null }) {
  const { isDesktop, mounted } = useIsDesktop(901);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    list,
    listMeta,
    loading,
    loadingMore,
    error,
    filters: storeFilters,
  } = useAppSelector((state) => state.activity);

  const [pageIndex, setPageIndex] = useState(1);
  const listQueryKeyRef = useRef("");
  const filtersLoaded = Boolean(storeFilters);

  const urlParams = useMemo(() => {
    if (!router.isReady) return {};
    return parseUrlListParams(router.query);
  }, [router.isReady, router.query]);

  const apiUrlParams = useMemo(() => pickUrlApiParams(urlParams), [urlParams]);
  const filterQueryKey = useMemo(
    () => JSON.stringify(router.query || {}),
    [router.query]
  );

  const apiFilters = useMemo(
    () => normalizeActivityFilters(storeFilters),
    [storeFilters]
  );

  const filterOptions = useMemo(
    () => ({
      activityTypes: apiFilters.activityTypes,
      subTypes: apiFilters.subTypes,
      cities: apiFilters.cities,
      includes: apiFilters.includes,
      timeOfDay: apiFilters.timeOfDay,
      cancellationPolicies: apiFilters.cancellationPolicies,
      instantConfirm: apiFilters.instantConfirm,
      reviewBands: buildReviewBands(apiFilters.reviewRatings),
      price: apiFilters.price,
      sortOptions: apiFilters.sortOptions,
      totalActivities: apiFilters.totalActivities,
      originCountry: apiFilters.originCountry,
    }),
    [apiFilters]
  );

  const { selection, budget, filterState, handlers, sessionReady, searchKey, searchEpoch } =
    useFilterSelection(filterOptions, urlParams, "activity");

  const defaultSort = filterOptions.sortOptions[0]?.value || "recommended";
  const [sortBy, setSortBy] = useState(defaultSort);
  const [activeSheet, setActiveSheet] = useState(null);
  const [draftSort, setDraftSort] = useState(defaultSort);
  const [draftBudget, setDraftBudget] = useState(budget);
  const [openSections, setOpenSections] = useState({
    categories: false,
    subTypes: false,
    cities: false,
    reviews: false,
    cancellation: false,
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const awaitingSearchLoadRef = useRef(false);
  const sawSearchFetchRef = useRef(false);

  useEffect(() => {
    const stored = loadListingFilters("activity");
    const storedEpoch = Number(stored?.epoch) || 0;
    if (
      searchEpoch <= storedEpoch &&
      stored?.sortBy &&
      isSameListingSearch(stored, "activity", searchKey)
    ) {
      setSortBy(stored.sortBy);
      return;
    }
    setSortBy(filterOptions.sortOptions[0]?.value || "recommended");
  }, [searchKey, searchEpoch, filterOptions.sortOptions]);

  useEffect(() => {
    if (filterOptions.sortOptions.length) {
      setSortBy((prev) =>
        filterOptions.sortOptions.some((option) => option.value === prev)
          ? prev
          : filterOptions.sortOptions[0].value
      );
    }
  }, [filterOptions.sortOptions]);

  useEffect(() => {
    if (!sessionReady) return;
    if (filterOptions.price.max <= 0) return;
    saveListingFilters("activity", {
      selection,
      budget,
      sortBy,
      query: router.query,
      searchKey,
      epoch: searchEpoch,
      boundsKey: getFilterBoundsKey({
        priceMin: filterOptions.price.min,
        priceMax: filterOptions.price.max,
      }),
    });
  }, [
    sessionReady,
    selection,
    budget,
    sortBy,
    router.query,
    searchKey,
    searchEpoch,
    filterOptions.price.min,
    filterOptions.price.max,
  ]);

  useEffect(() => {
    if (!router.isReady) return;
    dispatch(fetchActivityFilters(router.query));
  }, [dispatch, router.isReady, filterQueryKey]);

  const listQueryKey = useMemo(
    () =>
      JSON.stringify({
        apiUrlParams,
        filterState,
        sortBy,
      }),
    [apiUrlParams, filterState, sortBy]
  );

  const fetchActivitiesPage = useCallback(
    (page, append) => {
      dispatch(
        fetchActivitiesList({
          ...buildActivityListPayload({
            urlParams: apiUrlParams,
            filterState,
            pageIndex: page,
            pageSize: PAGE_SIZE,
            sortBy,
          }),
          append,
        })
      );
    },
    [dispatch, apiUrlParams, filterState, sortBy]
  );

  useEffect(() => {
    if (!router.isReady) return;
    if (!filtersLoaded) return;

    const queryChanged = listQueryKeyRef.current !== listQueryKey;
    listQueryKeyRef.current = listQueryKey;

    if (queryChanged) {
      setPageIndex(1);
      fetchActivitiesPage(1, false);
    }
  }, [router.isReady, filtersLoaded, listQueryKey, fetchActivitiesPage]);

  const mappedActivities = useMemo(
    () => (Array.isArray(list) ? list.map(mapActivity) : []),
    [list]
  );

  const hasListResult = filtersLoaded && !loading;
  const isListLoading = !router.isReady || !filtersLoaded || loading || searchLoading;
  const totalCount = hasListResult
    ? listMeta.totalCount
    : filterOptions.totalActivities || mappedActivities.length;
  const hasMore =
    mappedActivities.length > 0 &&
    (totalCount > mappedActivities.length ||
      (listMeta.totalPages > 0 && pageIndex < listMeta.totalPages));

  const loadMoreActivities = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageIndex + 1;
    setPageIndex(nextPage);
    fetchActivitiesPage(nextPage, true);
  }, [loading, loadingMore, hasMore, pageIndex, fetchActivitiesPage]);

  const pageTitle = getUrlPageTitle(urlParams);

  const {
    draft: searchDraft,
    locationOpen,
    initDraft,
    openLocationSearch,
    closeLocationSearch,
    selectLocation,
    setDestinationQuery,
    applySearch,
  } = useActivityEditSearch({
    urlParams,
    pageTitle,
  });

  const handleSearch = useCallback(() => {
    awaitingSearchLoadRef.current = true;
    sawSearchFetchRef.current = false;
    setSearchLoading(true);
    applySearch();
  }, [applySearch]);

  useEffect(() => {
    if (!awaitingSearchLoadRef.current) return;

    if (loading) {
      sawSearchFetchRef.current = true;
      return;
    }

    if (sawSearchFetchRef.current) {
      awaitingSearchLoadRef.current = false;
      sawSearchFetchRef.current = false;
      setSearchLoading(false);
    }
  }, [loading, listQueryKey]);

  useEffect(() => {
    if (!searchLoading) return undefined;

    const timeout = window.setTimeout(() => {
      if (!awaitingSearchLoadRef.current) return;
      awaitingSearchLoadRef.current = false;
      sawSearchFetchRef.current = false;
      setSearchLoading(false);
    }, 12000);

    return () => window.clearTimeout(timeout);
  }, [searchLoading]);

  const headerSearch = useMemo(
    () => getHeaderSearchSummary(urlParams, pageTitle),
    [urlParams, pageTitle]
  );

  const rangeProps = {
    price: filterOptions.price,
    budget,
    hasPriceRange: filterOptions.price.max > filterOptions.price.min,
  };

  const filterProps = {
    options: filterOptions,
    selection,
    ranges: rangeProps,
    openSections,
    handlers,
    onToggleSection: (key) =>
      setOpenSections((prev) => ({ ...prev, [key]: !prev[key] })),
  };

  const budgetStep =
    filterOptions.price.max - filterOptions.price.min > 1000
      ? Math.max(1, Math.round((filterOptions.price.max - filterOptions.price.min) / 100))
      : 1;

  const isSortActive = sortBy !== defaultSort;
  const isBudgetActive =
    budget[0] !== filterOptions.price.min || budget[1] !== filterOptions.price.max;
  const isFiltersActive =
    selection.activityTypeIds.length > 0 ||
    selection.subTypeIds.length > 0 ||
    selection.cityIds.length > 0 ||
    selection.cancellationPolicyIds.length > 0 ||
    selection.includesTypeIds.length > 0 ||
    selection.reviewRating > 0 ||
    selection.instantConfirm ||
    selection.timeOfDayValues.length > 0 ||
    isBudgetActive;

  const openMobileSheet = (sheet) => {
    if (sheet === "sort") setDraftSort(sortBy);
    if (sheet === "budget") setDraftBudget(budget);
    if (sheet === "search") {
      closeLocationSearch();
      initDraft();
    }
    setActiveSheet(sheet);
  };

  const closeMobileSheet = () => setActiveSheet(null);

  const applySortSheet = () => {
    setSortBy(draftSort);
    closeMobileSheet();
  };

  const applyBudgetSheet = () => {
    handlers.onBudget(draftBudget);
    closeMobileSheet();
  };

  const applySearchSheet = () => {
    closeMobileSheet();
    handleSearch();
  };

  useEffect(() => {
    if (!mounted || !isDesktop || !router.isReady) return;
    initDraft();
  }, [mounted, isDesktop, router.isReady, initDraft]);

  useBodyScrollLock(
    Boolean(activeSheet) || Boolean(locationOpen && !isDesktop)
  );

  return (
    <MainLayout hideHeader mobileListing>
      <div className={styles.page}>
        {mounted && isDesktop ? (
          <div className={`standard-padding`} style={{ background: "#FAFAFA", paddingTop: "20px", paddingBottom: "20px" }}>
            <DesktopListingSearch
              urlParams={urlParams}
              pageTitle={pageTitle}
              selection={searchDraft.selection}
              draftLabel={searchDraft.label}
              onSearch={handleSearch}
              isSearching={searchLoading}
              locationOpen={locationOpen}
              onDestinationOpen={openLocationSearch}
              onDestinationQueryChange={setDestinationQuery}
              onLocationClose={closeLocationSearch}
              onLocationSelect={selectLocation}
            />
          </div>
        ) : null}
        <Breadcrumbs />
        <div className={`standard-padding ${styles.desktopOnly}`}>

          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <FiltersPanel {...filterProps} showApply={false} />
            </aside>

            <div className={styles.main}>
              <div className={styles.resultsHead}>
                <p className={styles.count}>
                  {isListLoading ? "Loading activities..." : `${totalCount} Activities`}
                </p>
                {filterOptions.sortOptions.length ? (
                  <label className={styles.sortWrap}>
                    <span className={styles.sortBy}>
                      <IoSwapVertical className={styles.sortIcon} aria-hidden />
                      Sort By
                    </span>
                    <span className={styles.sortValueWrap}>
                      <select
                        className={styles.sortSelect}
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value)}
                      >
                        {filterOptions.sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <IoChevronDown className={styles.sortChevron} aria-hidden />
                    </span>
                  </label>
                ) : null}
              </div>

              <div className={styles.list}>
                {error ? <p className={styles.empty}>{String(error)}</p> : null}
                {isListLoading ? <ListingLoader /> : null}
                {!isListLoading &&
                  !error &&
                  mappedActivities.map((item) => <ActivityCard key={item.id} item={item} />)}
                {!isListLoading && !error && !mappedActivities.length ? (
                  <p className={styles.empty}>No activities match your filters.</p>
                ) : null}
                {!isListLoading && !error ? (
                  <ListLoadSentinel
                    hasMore={hasMore}
                    loading={loading}
                    loadingMore={loadingMore}
                    onLoadMore={loadMoreActivities}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.mobileOnly}>
          <header className={styles.mobileHeader}>
            <a href={withRegionPath("/activity")} className={styles.iconBtn} aria-label="Back">
              <IoArrowBackSharp fontSize={24} />
            </a>
            <button
              type="button"
              className={styles.searchPill}
              onClick={() => openMobileSheet("search")}
            >
              <span className={styles.searchPillText}>
                <span className={styles.searchPillLocation}>{headerSearch.location}</span>
              </span>
              <span className={styles.pencil} aria-hidden="true">
                <BiSolidEditAlt fontSize={20} />
              </span>
            </button>
            <CartNavLink className={styles.iconBtn} />
          </header>

          {isListLoading ? (
            <ListingLoader fullPage />
          ) : (
            <>
              <div className={styles.chipBar}>
                {filterOptions.sortOptions.length ? (
                  <button
                    type="button"
                    className={`${styles.chip}`}
                    onClick={() => openMobileSheet("sort")}
                  >
                    Sort <IoChevronDown />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`${styles.chip}`}
                  onClick={() => openMobileSheet("filters")}
                >
                  <HiOutlineAdjustmentsHorizontal /> Filters
                </button>
                {rangeProps.hasPriceRange ? (
                  <button
                    type="button"
                    className={`${styles.chip}`}
                    onClick={() => openMobileSheet("budget")}
                  >
                    Budget <IoChevronDown />
                  </button>
                ) : null}
              </div>

              <div className={styles.mobileList}>
                {error ? <p className={styles.empty}>{String(error)}</p> : null}
                {!error &&
                  mappedActivities.map((item) => (
                    <ActivityCard key={item.id} item={item} mobile />
                  ))}
                {!error && !mappedActivities.length ? (
                  <p className={styles.empty}>No activities match your filters.</p>
                ) : null}
                {!error ? (
                  <ListLoadSentinel
                    hasMore={hasMore}
                    loading={loading}
                    loadingMore={loadingMore}
                    onLoadMore={loadMoreActivities}
                  />
                ) : null}
              </div>
            </>
          )}

          {activeSheet === "sort" ? (
            <SortSheet
              options={filterOptions.sortOptions}
              value={draftSort}
              onChange={setDraftSort}
              onClose={closeMobileSheet}
              onApply={applySortSheet}
            />
          ) : null}

          {activeSheet === "budget" ? (
            <RangeFilterSheet title="Budget" onClose={closeMobileSheet} onApply={applyBudgetSheet}>
              <DualRangeSlider
                min={filterOptions.price.min}
                max={filterOptions.price.max}
                step={budgetStep}
                value={draftBudget}
                onChange={setDraftBudget}
                fixedLabels
                formatLabel={(val) => (
                  <>
                    {getCurrencySymbol("main", 12)}
                    {formatIndianPrice(val)}
                  </>
                )}
              />
            </RangeFilterSheet>
          ) : null}

          {activeSheet === "search" ? (
            <>
              <SearchSheet
                destination={searchDraft.label || pageTitle || "Activities"}
                onDestinationClick={openLocationSearch}
                onClose={closeMobileSheet}
                onApply={applySearchSheet}
                isSearching={searchLoading}
              />
              {locationOpen ? (
                <LocationSearchScreen
                  initialQuery={searchDraft.label || pageTitle || ""}
                  onClose={closeLocationSearch}
                  onSelect={selectLocation}
                />
              ) : null}
            </>
          ) : null}

          {activeSheet === "filters" ? (
            <MobileSheet
              title="Filters"
              onClose={closeMobileSheet}
              onClear={handlers.onClear}
              onApply={closeMobileSheet}
            >
              <FiltersPanel {...filterProps} showApply={false} compact />
            </MobileSheet>
          ) : null}
        </div>
        <DownloadNH />
        <SeeTheWorld
          title={initialListPageHeading?.heading?.title}
          text={initialListPageHeading?.heading?.description}
        />
      </div>
    </MainLayout>
  );
}
